import { useEffect, useState } from 'react'

import { astraTwMerge } from '../stories/Utilities/astraTwMerge'

type Fade = { top: boolean; bottom: boolean }

/**
 * Tracks which edges of a scroll container still have content beyond the fold.
 *
 * Returns a callback ref rather than taking a RefObject: FloatingPortal mounts its children on a
 * later render than the one where a dialog opens, so a ref read during that first effect would
 * still be null and the effect would never retry.
 */
export const useScrollFade = (enabled = true) => {
    const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
    const [fade, setFade] = useState<Fade>({ top: false, bottom: false })

    useEffect(() => {
        if (!enabled || !scrollElement) return

        const update = () => {
            const { scrollTop, scrollHeight, clientHeight } = scrollElement
            // A pixel of tolerance stops sub-pixel rounding from flickering the fades
            setFade({ top: scrollTop > 1, bottom: scrollTop + clientHeight < scrollHeight - 1 })
        }

        update()
        scrollElement.addEventListener('scroll', update, { passive: true })

        // The container resizing and its content changing are separate signals, and both move the fold
        // jsdom has no ResizeObserver, so unit tests rendering a dialog would otherwise crash
        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update)
        resizeObserver?.observe(scrollElement)
        const mutationObserver = new MutationObserver(update)
        mutationObserver.observe(scrollElement, { childList: true, subtree: true, characterData: true })

        return () => {
            scrollElement.removeEventListener('scroll', update)
            resizeObserver?.disconnect()
            mutationObserver.disconnect()
        }
    }, [scrollElement, enabled])

    return { setScrollElement, fade }
}

type ScrollFadesProps = {
    fade: Fade
    /** Prefixes the data-test hooks, so two open surfaces stay tellable apart */
    name?: string
}

/** Renders over the edges of a scroll container. Its parent needs `relative`. */
export const ScrollFades = ({ fade, name = 'dialog' }: ScrollFadesProps) => (
    <>
        <div
            aria-hidden
            data-test={`${name}-fade-top`}
            className={astraTwMerge(
                'pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-surface-base to-transparent transition-opacity',
                fade.top ? 'opacity-100' : 'opacity-0'
            )}
        />
        <div
            aria-hidden
            data-test={`${name}-fade-bottom`}
            className={astraTwMerge(
                'pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-surface-base to-transparent transition-opacity',
                fade.bottom ? 'opacity-100' : 'opacity-0'
            )}
        />
    </>
)
